import { Component, ElementRef, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import {
	ControlContainer,
	ControlValueAccessor,
	FormControl,
	FormGroupDirective,
	NG_VALUE_ACCESSOR,
	NgForm,
	UntypedFormControl,
	UntypedFormGroup,
} from '@angular/forms';
import { CmSettingsService, PortalCoreFeature } from '@ats/cm/core';
import { ErrorStateMatcher } from '@angular/material/core';

// https://stackoverflow.com/questions/45659742/angular4-no-value-accessor-for-form-control/45659791

// Because we are using NG_VALUE_ACCESSOR with Material chip list and Material chips are
// difficult to work with, we have to add a custom error state matcher to actually display the
// errors. Currently, you have to add a form control for the error state matcher to work, but we
// don't want to tie the control to anything because it would interfere with our NG_VALUE_ACCESSOR.
class CustomErrorStateMatcher implements ErrorStateMatcher {
	constructor(private readonly formControlName: string) {}

	isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
		const selectedControl = form.form.controls[this.formControlName];
		return (
			(selectedControl.dirty || selectedControl.touched) &&
			Object.values(selectedControl.errors || {}).some(error => error)
		);
	}
}

@Component({
    selector: 'cm-file-upload',
    templateUrl: './cm-file-upload.component.html',
    styleUrls: ['./cm-file-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CmFileUploadComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class CmFileUploadComponent implements ControlValueAccessor, OnInit {
	@ViewChild('fileInput', { read: ElementRef }) fileInput: ElementRef;
	@Input() formControlName: string;
	@Input() acceptedFileTypes: string = '*';
	@Input('isDisabled') _isDisabled: boolean = false;
	@Input() multiple: boolean = false;
	_value: File[] = [];
	readonly locFeature: PortalCoreFeature;
	customErrorStateMatcher: ErrorStateMatcher;
	formControl = new FormControl([]);

	formGroup: UntypedFormGroup;
	onChange = (_: File[]) => {};
	onTouched = () => {};

	get value() {
		return this._value;
	}

	set value(file: File[]) {
		this._value = file;
		this.onChange(file);
		this.onTouched();
	}

	get isDisabled(): boolean {
		return this._isDisabled;
	}

	set isDisabled(isDisabled: boolean) {
		this._isDisabled = isDisabled;
	}

	get control(): UntypedFormControl {
		return this.formGroup.get(this.formControlName) as UntypedFormControl;
	}

	get required(): boolean {
		if (!this.formGroup || !this.control) {
			return false;
		}
		const { validator } = this.control;
		if (validator) {
			const validation = validator(new UntypedFormControl());
			return validation !== null && validation.required === true;
		}
		return false;
	}

	constructor(private controlContainer: ControlContainer, private settings: CmSettingsService) {
		this.locFeature = this.settings.portalCoreFeature;
	}

	ngOnInit(): void {
		this.formGroup = this.controlContainer.control as UntypedFormGroup;
		this.customErrorStateMatcher = new CustomErrorStateMatcher(this.formControlName);
	}

	attachFile(event: any): void {
		const files = (event.target as HTMLInputElement).files;
		for (let i = 0; i < files.length; i++) {
			if (this.multiple && this.value) {
				this.writeValue([...this.value, files.item(i)]);
			} else {
				this.writeValue([files.item(i)]);
			}
		}
	}

	remove(index: number): void {
		this.writeValue(this.value.filter((file, i) => i !== index));
	}

	writeValue(value: File[]): void {
		this.value = value;
		if (!(value || []).length && this.fileInput) {
			const inputEl: HTMLInputElement = this.fileInput.nativeElement;
			inputEl.value = '';
		}
	}

	registerOnChange(fn): void {
		this.onChange = fn;
	}

	registerOnTouched(fn): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}
}
